import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import { Box, Stack } from '@mui/material'
import ChartSkeleton from 'components/global/CUK/Skeletons/ChartSkeleton'
import GraphModal from 'components/GraphModal'
import { GraphSectionProps } from '../../utils/types'
import { formatTimeForDisplay, createTimeRangeParams } from '../../utils/helpers'
import BandwidthGraph from './graphs/BandwidthGraph'
import LatencyGraph from './graphs/LatencyGraph'
import LossGraph from './graphs/LossGraph'
import DroppedPacketsGraph from './graphs/DroppedPacketsGraph'
import { getDateRangeKind, trackEvent } from 'analytics'

export const OPERATION_GET_WIRE_LINE_GRAPH_DATA = gql`
  query FindWireLineGraphData($lineId: ID!, $timeRange: TimeRangeInput!) {
    wireLineGraph: findWireLineGraphData(lineId: $lineId, timeRange: $timeRange) {
      timestamps
      bandwidth {
        download
        upload
      }
      latency {
        ping
      }
      loss {
        loss
      }
      droppedPackets {
        download
        upload
        rxPackets
        txPackets
      }
    }
  }
`

const GraphSection: React.FC<GraphSectionProps> = ({ lineId }) => {
  const { t, i18n } = useTranslation()
  const [expandedGraph, setExpandedGraph] = useState<string | null>(null)
  const [modalTimeRangePreset, setModalTimeRangePreset] = useState<string>('-1d')
  const [modalDateRange, setModalDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  })
  const [isModalCustomRange, setIsModalCustomRange] = useState<boolean>(false)

  const { loading, data } = useQuery(OPERATION_GET_WIRE_LINE_GRAPH_DATA, {
    variables: { 
      lineId, 
      timeRange: { preset: '-1d' }
    },
    fetchPolicy: 'network-only'
  })

  const {
    loading: modalLoading,
    data: modalData,
    refetch: refetchModalData
  } = useQuery(OPERATION_GET_WIRE_LINE_GRAPH_DATA, {
    variables: { 
      lineId, 
      timeRange: isModalCustomRange 
        ? createTimeRangeParams('', modalDateRange, true)
        : { preset: modalTimeRangePreset }
    },
    fetchPolicy: 'network-only',
    skip: !expandedGraph
  })

  const handleModalTimeRangeChange = (newTimeRange: string) => {
    if (newTimeRange !== modalTimeRangePreset || isModalCustomRange) {
      setModalTimeRangePreset(newTimeRange)
      setIsModalCustomRange(false)
      setModalDateRange({ start: null, end: null })
      refetchModalData({ 
        lineId, 
        timeRange: { preset: newTimeRange }
      })
      trackEvent('wan_graph_period_changed', {
        graph_type: expandedGraph || 'unknown',
        period: newTimeRange
      })
    }
  }

  const handleCloseModal = () => {
    setExpandedGraph(null)
  }

  const handleOpenModal = (graphType: string) => {
    trackEvent('wan_graph_opened', {
      graph_type: graphType
    })
    setExpandedGraph(graphType)
    setModalTimeRangePreset('-1d')
    setModalDateRange({ start: null, end: null })
    setIsModalCustomRange(false)
  }

  const handleModalDateRangeSubmit = (start: Date | null, end: Date | null) => {
    if (start && end) {
      setModalDateRange({ start, end })
      setIsModalCustomRange(true)
      setModalTimeRangePreset('')
      refetchModalData({ 
        lineId, 
        timeRange: createTimeRangeParams('', { start, end }, true)
      })
      trackEvent('wan_graph_period_changed', {
        graph_type: expandedGraph || 'unknown',
        period: 'custom',
        date_range_kind: getDateRangeKind(start, end)
      })
    }
  }

  const handleModalDateRangeReset = () => {
    setModalDateRange({ start: null, end: null })
    setIsModalCustomRange(false)
    setModalTimeRangePreset('-1d')
    refetchModalData({ 
      lineId, 
      timeRange: { preset: '-1d' }
    })
    trackEvent('wan_graph_period_changed', {
      graph_type: expandedGraph || 'unknown',
      period: '-1d'
    })
  }

  const droppedPacketsPercentage = (drops: number[], packets: number[]): number[] => {
    return drops.map((drop, index) => {
      const packet = packets[index] || 0
      if (packet === 0 || packet === null || drop === null) return 0
      return (drop / packet) * 100
    })
  }

  const graphData = useMemo(() => {
    if (!data?.wireLineGraph) return []
    const { timestamps, bandwidth, latency, loss, droppedPackets } = data.wireLineGraph

    const droppedPacketsDownload = droppedPackets ? 
      droppedPacketsPercentage(droppedPackets.download, droppedPackets.rxPackets) : 
      timestamps.map(() => 0)
    
    const droppedPacketsUpload = droppedPackets ? 
      droppedPacketsPercentage(droppedPackets.upload, droppedPackets.txPackets) : 
      timestamps.map(() => 0)

    return timestamps.map((timestamp: string, index: number) => {
      const formattedTime = formatTimeForDisplay(timestamp)
      return {
        timestamp,
        time: formattedTime,
        formattedTime,
        download: bandwidth.download[index] || 0,
        upload: bandwidth.upload[index] || 0,
        ping: latency.ping[index] || 0,
        loss: loss.loss[index] || 0,
        droppedPacketsDownload: droppedPacketsDownload[index] || 0,
        droppedPacketsUpload: droppedPacketsUpload[index] || 0
      }
    })
  }, [data, i18n.language])

  const modalGraphData = useMemo(() => {
    if (!modalData?.wireLineGraph) return []
    const { timestamps, bandwidth, latency, loss, droppedPackets } = modalData.wireLineGraph

    const droppedPacketsDownload = droppedPackets ? 
      droppedPacketsPercentage(droppedPackets.download, droppedPackets.rxPackets) : 
      timestamps.map(() => 0)
    
    const droppedPacketsUpload = droppedPackets ? 
      droppedPacketsPercentage(droppedPackets.upload, droppedPackets.txPackets) : 
      timestamps.map(() => 0)

    return timestamps.map((timestamp: string, index: number) => {
      const formattedTime = formatTimeForDisplay(timestamp)
      return {
        timestamp,
        time: formattedTime,
        formattedTime,
        download: bandwidth.download[index] || 0,
        upload: bandwidth.upload[index] || 0,
        ping: latency.ping[index] || 0,
        loss: loss.loss[index] || 0,
        droppedPacketsDownload: droppedPacketsDownload[index] || 0,
        droppedPacketsUpload: droppedPacketsUpload[index] || 0
      }
    })
  }, [modalData, i18n.language])

  const renderExpandedGraph = () => {
    if (!expandedGraph) return null

    const title = expandedGraph === 'bandwidth'
      ? t('wan.graphs.bandwidth.title')
      : expandedGraph === 'latency'
        ? t('wan.graphs.latency.title')
        : expandedGraph === 'loss'
          ? t('wan.graphs.loss.title')
          : t('wan.graphs.dropped_packets.title')

    return (
      <GraphModal
        open={Boolean(expandedGraph)}
        onClose={handleCloseModal}
        title={title}
        timeRangePreset={modalTimeRangePreset}
        isCustomRange={isModalCustomRange}
        dateRange={modalDateRange}
        onTimeRangeChange={handleModalTimeRangeChange}
        onStartDateChange={value => setModalDateRange(prev => ({ ...prev, start: value }))}
        onEndDateChange={value => setModalDateRange(prev => ({ ...prev, end: value }))}
        onDateRangeSubmit={handleModalDateRangeSubmit}
        onDateRangeReset={handleModalDateRangeReset}
      >
        <Box sx={{ height: '100%' }}>
          {modalLoading ? (
            <Box sx={{ height: '100%', width: '100%' }}>
              <ChartSkeleton />
            </Box>
          ) : (
            <>
              {expandedGraph === 'bandwidth' && (
                <BandwidthGraph
                  data={modalGraphData}
                  legendData={graphData}
                  isExpanded={true}
                  onExpand={() => { }}
                  hideTitle={true}
                />
              )}
              {expandedGraph === 'latency' && (
                <LatencyGraph
                  data={modalGraphData}
                  legendData={graphData}
                  isExpanded={true}
                  onExpand={() => { }}
                  hideTitle={true}
                />
              )}
              {expandedGraph === 'loss' && (
                <LossGraph
                  data={modalGraphData}
                  legendData={graphData}
                  isExpanded={true}
                  onExpand={() => { }}
                  hideTitle={true}
                />
              )}
              {expandedGraph === 'dropped_packets' && (
                <DroppedPacketsGraph
                  data={modalGraphData}
                  legendData={graphData}
                  isExpanded={true}
                  onExpand={() => { }}
                  hideTitle={true}
                />
              )}
            </>
          )}
        </Box>
      </GraphModal>
    )
  }

  if (loading) {
    return (
      <Box sx={{ mt: 1 }}>
        <Stack spacing={3}>
          {[1, 2, 3, 4].map((index) => (
            <Box 
              key={index}
              sx={{ 
                height: 200, 
                border: '1px solid #E5E7EB', 
                borderRadius: '16px',
                bgcolor: 'background.paper'
              }}
            >
              <ChartSkeleton />
            </Box>
          ))}
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 1, pb: 3 }}>
      <Stack spacing={3}>
        <BandwidthGraph
          data={graphData}
          legendData={graphData}
          isExpanded={false}
          onExpand={() => handleOpenModal('bandwidth')}
        />
        <LatencyGraph
          data={graphData}
          legendData={graphData}
          isExpanded={false}
          onExpand={() => handleOpenModal('latency')}
        />
        <LossGraph
          data={graphData}
          legendData={graphData}
          isExpanded={false}
          onExpand={() => handleOpenModal('loss')}
        />
        <DroppedPacketsGraph
          data={graphData}
          legendData={graphData}
          isExpanded={false}
          onExpand={() => handleOpenModal('dropped_packets')}
        />
      </Stack>
      
      {renderExpandedGraph()}
    </Box>
  )
}

export default GraphSection
